import { Box, Card, IconButton, Modal, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { extractSvgAttributes, processSvgContent } from '../../Signet/Studio/utils/svgUtils';
import InnerShadowFilter from '../../Signet/Studio/components/svg/InnerShadowFilter';
import TextureFilter from '../../Signet/Studio/components/svg/TextureFilter';
import OuterShadowFilter from '../../Signet/Studio/components/svg/OuterShadowFilter';
import { MATERIAL_TEXTURE_MAP } from '../../Signet/Studio/constants';
import { useDesignPatternStudio } from '../DesignPatternStudioContext';
import Warp from 'warpjs';
import { useTexture } from '../../Shared/TextureContext';
import ManualWarpModal from './ManualWarpModal';

const useStyles = makeStyles((theme) => ({
  variantCard:  (props) => ({
    height: '100%',
    position: 'relative',
    border: props?.isPreview ? 'none' : `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: 'none',
    transition: 'all 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      '& .delete-button': {
        opacity: 1,
      },
    },
  }),
  materialTitle: {
    padding: theme.spacing(1),
    textAlign: 'center',
    textTransform: 'uppercase',
    fontWeight: 600,
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  imageContainer: (props) => ({
    height: props.isLarge ? 350 : 150,
    width: props.isLarge ? 350 : 150,
    backgroundColor: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(0.5),
    '& img, & svg': {
      maxWidth: '100%',
      maxHeight: '90%',
      objectFit: 'contain',
    },
  }),
  deleteButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    opacity: 0,
    transition: 'opacity 0.2s ease',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    '&:hover': {
      backgroundColor: theme.palette.error.light,
      color: theme.palette.error.contrastText,
    },
  },
  variantInfo: {
    padding: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    gap: theme.spacing(0.75),
    boxSizing: 'border-box',
    alignItems: 'center',
    justifyContent: 'space-around',
    width: '100%',
    overflowY: 'hidden',
    overflowX: 'auto',
  },
  tag: {
    padding: theme.spacing(0.5),
    borderRadius: 12,
    fontSize: '0.7rem',
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
    maxWidth: '32%',
    display: 'inline-block',
    textAlign: 'center',
    textTransform: 'capitalize',
    width: '100%',
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalContent: {
    position: 'relative',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(4),
    outline: 'none',
    maxWidth: '90vw',
    maxHeight: '90vh',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  fullscreenButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.contrastText,
    },
  },
  previewContainer: {
    width: '675px',
    height: '600px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      width: '100%',
      height: '100%',
    },
  },
  widthUnit: {
    fontSize: '0.8rem',
    fontWeight: 500,
    textTransform: 'lowercase',
  },
}));

const SVG_SIZE = 1200;

const PreviewVariantCard = ({
  mapping,
  config,
  isPreview = false,
  svgFile,
  onDelete = () => {},
  isLarge = false,
  showPathPreview = true,
  className = "",
  isCapturingImage = false,
  resetScale = () => {},
  updateManualWarp,
}) => {
  const classes = useStyles({ isLarge, isPreview });
  const svgRef = useRef(null);
  const gRef = useRef(null);
  const pathRef = useRef(null);

  const warpInstanceRef = useRef(null);
  const animationFrameIdRef = useRef(null);
  const warpValueRef = useRef(0);
  const displacementValueRef = useRef(50);
  const originalPatternRef = useRef(null); // Original uploaded pattern (never changes, stored in ref)
  const [svgContent, setSvgContent] = useState(null);
  const [transforms, setTransforms] = useState({
    contentTransform: '',
    pathTransform: '',
    pathDimensions: null,
    maskTransform: '',
  });
  const [showPreview, setShowPreview] = useState(false);
  const [showManualWarpModal, setShowManualWarpModal] = useState(false);
  const { state, dispatch } = useDesignPatternStudio();
  const [isHovered, setIsHovered] = useState(false);
  const { textures } = useTexture();

  const ringImage = useMemo(() => {
    return mapping?.width?.base_ring_url || "";
  }, [mapping]);

  // Load SVG content when element becomes visible and file is available
  useEffect(() => {
    if (svgFile) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = processSvgContent(config?.manualWarp?.manualWarpSvg || e.target.result, mapping.color?.hexcode || '#FFF');
        setSvgContent(content);
        originalPatternRef.current = e.target.result; // Store original pattern in ref
      };
      reader.readAsText(svgFile);
    }
  }, [svgFile, mapping.color]);

  // Calculate transforms when content is loaded
  useEffect(() => {
    if (!pathRef.current || !config || !gRef.current || !svgContent) return;

    const calculateTransforms = () => {
      setTimeout(() => {
        const bbox = pathRef.current?.getBBox();
        if (!bbox || !isFinite(bbox.width) || !isFinite(bbox.height)) return;

        // Calculate path transform
        const maxShapeDimension = Math.max(config.width, config.height);
        const pathMaxDimension = Math.max(bbox.width, bbox.height);
        const pathScale = maxShapeDimension / pathMaxDimension;

        const scaledWidth = bbox.width * pathScale;
        const scaledHeight = bbox.height * pathScale;
        const translateX = (SVG_SIZE - scaledWidth) / 2 - bbox.x * pathScale;
        const translateY = (SVG_SIZE - scaledHeight) / 2 - bbox.y * pathScale;

        // Calculate mask transform with padding consideration
        let maskTransform = `translate(${translateX}, ${translateY}) scale(${pathScale})`;

        if (config?.padding > 0) {
          const centerX = bbox.x + bbox.width / 2;
          const centerY = bbox.y + bbox.height / 2;
          const paddingScale = 1 - config?.padding / 1000;

          maskTransform = [
            `translate(${translateX}, ${translateY})`,  // Positions the path in the SVG coordinate system
            `scale(${pathScale})`,                      // Scales the path to to match the desired size
            `translate(${centerX}, ${centerY})`,        // Moves the origin to the center of the path
            `scale(${paddingScale})`,                   // Scales down the path (making it smaller for padding effect)
            `translate(${-centerX}, ${-centerY})`,     // Moves the origin back to the original position
          ].join(' ');
        }
        if (config?.manualWarp?.controlPoints?.length > 0 && config?.manualWarp?.isEnabled) {
          // Apply pattern position and scale for manual warp
          const patternPos = config?.manualWarp?.patternPosition || { x: 0, y: 0 };
          const patternScale = config?.manualWarp?.patternScale || 1;
          const viewBoxScale = config?.manualWarp?.viewBoxScale || 1;

          // Get content bounding box for fallback sizing
          const contentBBox = gRef.current.getBBox();
          if (!contentBBox || !isFinite(contentBBox.width) || !isFinite(contentBBox.height)) return;
          // Use the SVG viewBox center to match WarpSvg's transformOrigin: 'center'
          const svgAttributes = extractSvgAttributes(svgContent);
          const viewBoxParts = svgAttributes.viewBox?.split(/[\s,]+/).map(Number) || [];
          let viewBoxX = 0;
          let viewBoxY = 0;
          let viewBoxWidth = contentBBox.width;
          let viewBoxHeight = contentBBox.height;

          if (viewBoxParts.length >= 4 && viewBoxParts.every(isFinite)) {
            [viewBoxX, viewBoxY, viewBoxWidth, viewBoxHeight] = viewBoxParts;
          } else {
            const parseNumeric = (value) => {
              if (!value) return NaN;
              const parsed = Number(String(value).replace(/[^0-9.\-]/g, ''));
              return Number.isFinite(parsed) ? parsed : NaN;
            };
            const widthAttr = parseNumeric(svgAttributes.width);
            const heightAttr = parseNumeric(svgAttributes.height);
            if (Number.isFinite(widthAttr)) viewBoxWidth = widthAttr;
            if (Number.isFinite(heightAttr)) viewBoxHeight = heightAttr;
          }

          const centerX = (viewBoxWidth / 2) * viewBoxScale;
          const centerY = (viewBoxHeight / 2) * viewBoxScale;

          /**
           * The saved SVG already includes an inner wrapper <g> with:
           * translate(warpTranslateX, warpTranslateY) scale(warpScale)
           * To keep patternPosition/patternScale in the original coordinate system,
           * apply the inverse of that inner transform on the outer group.
           *
           * ******* Below is commented out because we are not using warp Offset anymore******
           */
          // const { warpTranslateX, warpTranslateY, warpScale } = config?.manualWarp?.warpOffset || { warpTranslateX: 0, warpTranslateY: 0, warpScale: 1 };
          // const hasWarpOffset = warpTranslateX !== 0 || warpTranslateY !== 0 || warpScale !== 1;

          console.log('>>>>VIMAL', {
            patternPos,
            patternScale,
            viewBoxScale,
            centerX,
            centerY,
            viewBox: { viewBoxX, viewBoxY, viewBoxWidth, viewBoxHeight },
            contentBBox: { x: contentBBox.x, y: contentBBox.y, width: contentBBox.width, height: contentBBox.height },
          })
          /**
           * Manual warp preview mirrors the WarpSvg transform stack (array order shown).
           * Note: SVG applies transforms right-to-left; bottom entries execute first.
           * 1) translateX/translateY to center content in SVG_SIZE.
           * 2) scale(pathScale) to match ring size.
           * 3) translate(patternPos) in ring units (no scale applied).
           * 4-6) apply patternScale around the viewBox center.
           * 7) scale(viewBoxScale) for oversized source SVGs.
           * 8) translate(-viewBoxX, -viewBoxY) to normalize non-zero viewBox origins.
           */
          const transformsList = [
            `translate(${translateX}, ${translateY})`,
            `scale(${pathScale})`,
            `translate(${patternPos.x}, ${patternPos.y})`,
            `translate(${centerX}, ${centerY})`,
            `scale(${patternScale})`,
            `translate(${-centerX}, ${-centerY})`,
            `scale(${viewBoxScale})`,
            `translate(${-viewBoxX}, ${-viewBoxY})`,
          ];
          /*
            * ******* Below is commented out because we are not using warp Offset anymore******
          if (hasWarpOffset) {
            transformsList.push(`scale(${warpScale}) translate(${-warpTranslateX}, ${-warpTranslateY})`);
          }*/

          setTransforms({
            pathTransform: `translate(${translateX}, ${translateY}) scale(${pathScale})`,
            contentTransform: transformsList.join(' '),
            pathDimensions: { width: scaledWidth, height: scaledHeight, scale: pathScale },
            maskTransform: maskTransform,
          });
          return;
        }

        // Calculate content transform
        const contentBBox = gRef.current.getBBox();
        if (!contentBBox || !isFinite(contentBBox.width) || !isFinite(contentBBox.height)) return;

        // Additional user scale factor (like if config.scale = 20 => +20%)
        const userScaleFactor = 1 + (config?.scale || 0) / 100;

        // The real "available" size is the shape's width/height times userScaleFactor
        const availableW = config.width * userScaleFactor;
        const availableH = config.height * userScaleFactor;

        // Compute how much to scale the shape to fit in that area
        const shapeFitScale = Math.min(
          availableW / contentBBox.width,
          availableH / contentBBox.height
        );

        // Combine with pathScale to align with the main path in the root coordinate system
        const finalShapeScale = shapeFitScale * pathScale;

        // Center the shape in SVG_SIZE × SVG_SIZE
        const finalW = contentBBox.width * finalShapeScale;
        const finalH = contentBBox.height * finalShapeScale;

        const contentTranslateX = (SVG_SIZE - finalW) / 2 - contentBBox.x * finalShapeScale;
        const contentTranslateY = (SVG_SIZE - finalH) / 2 - contentBBox.y * finalShapeScale;

        // Calculate shape's center point for rotation
        const shapeCenterX = contentBBox.x + contentBBox.width / 2;
        const shapeCenterY = contentBBox.y + contentBBox.height / 2;

        // Get rotation angle from config
        const rotation = config?.shape_rotation || 0;

        // Get shape_x and shape_y for position adjustment (defaulting to 0)
        const shapeXOffset = config?.shape_x || 0;
        const shapeYOffset = config?.shape_y || 0;

        setTransforms({
          pathTransform: `translate(${translateX}, ${translateY}) scale(${pathScale})`,
          contentTransform: [
            `translate(${contentTranslateX}, ${contentTranslateY})`,
            // Apply x and y offsets after initial positioning but before rotation
            `translate(${shapeXOffset}, ${-shapeYOffset})`,
            `translate(${shapeCenterX * finalShapeScale}, ${shapeCenterY * finalShapeScale})`,
            `rotate(${rotation})`,
            `translate(${-shapeCenterX * finalShapeScale}, ${-shapeCenterY * finalShapeScale})`,
            `scale(${finalShapeScale})`
          ].join(' '),
          pathDimensions: { width: scaledWidth, height: scaledHeight, scale: pathScale },
          maskTransform: maskTransform,
        });
      }, 100);
    };

    // Calculate transforms immediately without resetting WarpJS
    requestAnimationFrame(calculateTransforms);
  }, [config, svgContent]);


  // Calculate group transform based on hover state
  const getGroupTransform = useCallback(
    (config, isHovered) => {
      if (!config || !isHovered || !transforms.pathDimensions) return '';

      const { rotation = 0, x = 0, y = 0 } = config;
      return `
        rotate(${-rotation})
        translate(${x}, ${-y})
    `;
    },
    [transforms.pathDimensions],
  );

  const handlePreviewClick = () => {
    setShowPreview(true);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
  };

  const handleDelete = (event) => {
    event.stopPropagation();
    event.preventDefault();
    onDelete?.();
  };

  const handleFullscreenClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setShowManualWarpModal(true);
  };

  const handleCloseManualWarp = () => {
    setShowManualWarpModal(false);
  };

  // Add background image pattern ID
  const backgroundPatternId = useMemo(
    () => `ring-background-${mapping?.material?.id}-${mapping?.profile?.sku}-${mapping?.width?.sku}`,
    [mapping],
  );

  const textureUrl = useMemo(() => {
    const textureType = state.insideFeature ? 'sleeve' : 'band';
    const textureData = textures[textureType];
    if (!textureData) return null;

    const textureKey = MATERIAL_TEXTURE_MAP[mapping.material.sku_code] || MATERIAL_TEXTURE_MAP[mapping.color?.sku_code?.replace('CER-', '')];
    // Handle material-based texture for NONE color SKU
    if (mapping.color?.sku_code === 'NONE' && mapping.material?.sku_code && textureKey) {
      return textureData?.[textureKey]?.preview_url;
    } else if (mapping.color?.sku_code === 'NONE' && mapping.material?.sku_code && !textureKey || state.insideFeature) {
      return textureData?.[mapping.material?.sku_code]?.preview_url;
    }

    // Handle regular color-based texture
    // TODO: Remove this once we have the new texture mapping for all colors
    if (isPreview) {
      return textureData["T"]?.preview_url;
    } else {
      // if color name starts with "CER-" then remove it from the sku else keep it as is . eg. CER-BLACK => BLACK
      const textureKey = MATERIAL_TEXTURE_MAP[mapping.color?.sku_code?.replace('CER-', '')] || mapping.color?.sku_code?.replace('CER-', '');
      return textureData[textureKey]?.preview_url;
    }
  }, [textures, mapping.color?.sku_code, mapping.material?.sku_code, isPreview, state.insideFeature]);

  const findTheBoundaries = useCallback(() => {
    if (!gRef.current) return;
    const { xMin, xMax, yMin, yMax } = [...gRef.current.children].reduce(
      (acc, el) => {
        const { x, y, width, height } = el.getBBox();
        if (!acc.xMin || x < acc.xMin) acc.xMin = x;
        if (!acc.xMax || x + width > acc.xMax) acc.xMax = x + width;
        if (!acc.yMin || y < acc.yMin) acc.yMin = y;
        if (!acc.yMax || y + height > acc.yMax) acc.yMax = y + height;
        return acc;
      },
      {}
    );

    return {
      xMin,
      yMin,
      x: xMax - xMin,
      y: yMax - yMin,
    };
  }, []);

  // Effect to update the warp value ref when config changes
  useEffect(() => {
    warpValueRef.current = Number(config?.warp) || 0;
  }, [config?.warp]);

  useEffect(() => {
    displacementValueRef.current = Number(config?.displacement) || 50;
  }, [config?.displacement]);

  // Effect for setting up WarpJS and the animation loop based on SVG content
  useEffect(() => {
    // Cleanup function: Stop animation and clear refs
    const cleanup = () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
      // Clear warp instance when cleaning up
      if (warpInstanceRef.current) {
        warpInstanceRef.current = null;
      }
    };

    // --- Ensure previous animation is stopped before setting up new ---
    cleanup();

    // Add a small delay to ensure the DOM has updated and gRef.current points to the correct element
    const setupWarpJS = () => {
      if (gRef.current && svgContent) {
        const svg = gRef.current;
        const svgData = findTheBoundaries();

        // Add more robust check for valid boundaries
        if (!svgData || !isFinite(svgData.x) || svgData.x <= 0 || !isFinite(svgData.y) || svgData.y <= 0) {
            console.warn("WarpJS: Invalid boundaries found, skipping warp setup.", svgData);
            return cleanup;
        }

        // Always create a new WarpJS instance for new SVG content
        warpInstanceRef.current = new Warp(svg);
        warpInstanceRef.current.interpolate(8);
        // Initial transform - store original y coordinates. Crucial step.
        warpInstanceRef.current.transform(([x, y]) => [x, y, y]);
        console.log("WarpJS: Instance created and oy stored for", showPreview ? "modal" : "card");

        const warpInstance = warpInstanceRef.current;

        function animate() {
          if (!warpInstanceRef.current) return;
          if (!svgData) return;

          const warpValue = warpValueRef.current;
          const displacementValue = displacementValueRef.current;
          // Handle the warp value range: -50 to +50, where 0 is normal
          let displacement = 0;
          let verticalOffset = 0;

          if (warpValue !== 0) {
            // Convert warp value to displacement factor
            const displacementFactor = warpValue / (displacementValue || 50);
            // Calculate maximum displacement
            const maxDisplacement = svgData.y * 0.3; // 30% of SVG height
            // Apply displacement
            displacement = displacementFactor * maxDisplacement;
            // Calculate vertical offset to prevent clipping
            verticalOffset = -displacement * 0.8;
          }

          warpInstance.transform(([x, y, oy]) => {
            if (displacement === 0) {
              // Return to original position when warp is 0
              return [x, oy, oy];
            }

            // Calculate relative x position (0 to 1)
            const relX = (x - svgData.xMin) / svgData.x;
            const clampedRelX = Math.max(0, Math.min(1, relX));

            // Create sine wave for smooth arc effect
            const arcFactor = Math.sin(clampedRelX * Math.PI);

            // Apply displacement with arc factor and vertical offset
            const newY = oy + displacement * arcFactor + verticalOffset;

            return [x, newY, oy];
          });

          animationFrameIdRef.current = requestAnimationFrame(animate);
        }

        // Start the animation
        animate();
        // Return the cleanup function
        return cleanup;
      } else {
        return cleanup;
      }
    };

    // Use double requestAnimationFrame to ensure DOM has updated
    const timeoutId = requestAnimationFrame(() => {
      requestAnimationFrame(setupWarpJS);
    });

    return () => {
      cleanup();
      cancelAnimationFrame(timeoutId);
    };
  }, [svgContent, findTheBoundaries, showPreview]);

  /**
   * On Save save controlPoints / meshComplexity / interpolation
   * use those when opening the modal originalPatternRef.current + controlPoints + meshComplexity + interpolation.
   */
  const handleSave = (warpedSvg, data) => {
    const payload = {
      meshComplexity: data.meshComplexity || 3,
      interpolation: data.interpolation || 50,
      controlPoints: data.controlPoints || [],
      manualWarpSvg: warpedSvg,
      patternPosition: data.patternPosition || { x: 0, y: 0 },
      patternScale: data.patternScale || 1,
      viewBoxScale: data.viewBoxScale || 1,
      // warpOffset: data.warpOffset || { warpTranslateX: 0, warpTranslateY: 0, warpScale: 1 },
      history: data.history || [],
      historyIndex: data.historyIndex ?? -1,
    }
    resetScale();
    setSvgContent(processSvgContent(warpedSvg, mapping.color?.hexcode || '#FFF'));
    updateManualWarp?.(payload);
    handleCloseManualWarp();
  };

  const renderSVG = (isHovered) => {
    if (!svgFile || !svgContent) return null;

    // Treat the SVG as hovered when capturing
    const isEffectivelyHovered = isHovered || isCapturingImage;

    return (
      <svg
        {...extractSvgAttributes(svgContent)}
        width="100%"
        height="100%"
        viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
        preserveAspectRatio="xMidYMid meet"
        style={{
          // border: showPathPreview && !isEffectivelyHovered ? '1px solid #CCC' : 'none',
          padding: showPathPreview && isHovered && !isCapturingImage ? '1px' : '0',
        }}
        className={`variant-svg ${className || ''}`}
        data-profile={mapping?.profile?.sku_code}
        data-width={mapping?.width?.sku_code}
        data-color={mapping?.color?.sku_code?.replace('CER-', '')}
        data-material={JSON.stringify({
          id: mapping?.material?.id,
          name: mapping?.material?.name,
          sku: MATERIAL_TEXTURE_MAP[mapping?.material?.sku_code] || mapping?.material?.sku_code,
        })}
        data-engraving={state.designPatternId?.sku_code}
        data-layer={state.insideFeature ? 'sleeve' : 'pattern'}
      >
        <defs>
          <InnerShadowFilter id={`innerShadow-${mapping.key}`} showStroke={true} shadowColor={'#000000'} strength={config?.depth || 'standard'} />
          <OuterShadowFilter
            id={`outerShadow-${mapping.key}`}
            shadowColor="#000000"
            strength={config?.depth || 'standard'}
          />
          <TextureFilter
            id={`texture-${mapping.key}`}
            textureUrl={textureUrl}
            patternType={state.insideFeature ? 'sleeve' : 'band'}
          />
          {/* Only add pattern if not capturing and is hovered */}
          {isHovered && !isCapturingImage && ringImage && (
            <pattern
              id={`${backgroundPatternId}-${mapping.key}`}
              patternUnits="userSpaceOnUse"
              width={SVG_SIZE}
              height={SVG_SIZE}
              className="ring-background"
            >
              <image
                href={decodeURIComponent(ringImage)}
                width={SVG_SIZE}
                height={SVG_SIZE}
                preserveAspectRatio="xMidYMid meet"
              />
            </pattern>
          )}
          <mask
            id={`mask-${mapping.key}`}
            maskUnits="userSpaceOnUse"
            maskContentUnits="userSpaceOnUse"
            x="-80" y="0" width="1360" height="1200"
          >
            <rect x="0" y="0" width="1200" height="1200" fill="black" />
            <path
              d={config?.path}
              fill="white"
              transform={transforms.maskTransform}
              stroke="none"
            />
          </mask>
        </defs>
        {/* Only show background ring image if not capturing and is hovered */}
        {isHovered && !isCapturingImage && ringImage && (
          <rect width={SVG_SIZE} height={SVG_SIZE} fill={`url(#${backgroundPatternId}-${mapping.key})`} />
        )}
        {/* Modified main content group */}
        <g transform={getGroupTransform(config, isEffectivelyHovered)} style={{ transformOrigin: 'center' }} mask={`url(#mask-${mapping.key}-shape-mask)`}>
          <g transform={transforms.pathTransform}>
            <path
              ref={pathRef}
              d={config?.path}
              width={config?.width}
              height={config?.height}
              stroke={showPathPreview && !isEffectivelyHovered ? '#000' : 'transparent'}
              fill="transparent"
            />
          </g>
          <g
            mask={config?.padding > 0 ? `url(#mask-${mapping.key})` : null}
          >
            {config?.manualWarp?.isEnabled ? (
              <g filter={`url(#texture-${mapping.key}) ${state.insideFeature ? "" : `url(#innerShadow-${mapping.key})`}`}>
                <g
                  ref={gRef}
                  transform={transforms.contentTransform}
                  dangerouslySetInnerHTML={{
                    __html: svgContent.replace(/<svg[^>]*>|<\/svg>/g, ''),
                  }}
                  fill={state.insideFeature ? "transparent" : "white"}
                  className={state.insideFeature ? 'engraving-svg' : ''}
                />
              </g>
            ) : (
              <g
                ref={gRef}
                transform={transforms.contentTransform}
                filter={`url(#texture-${mapping.key}) ${state.insideFeature ? "" : `url(#innerShadow-${mapping.key})`}`}
                dangerouslySetInnerHTML={{
                  __html: svgContent.replace(/<svg[^>]*>|<\/svg>/g, ''),
                }}
                fill={state.insideFeature ? "transparent" : "white"}
                className={state.insideFeature ? 'engraving-svg' : ''}
              />
            )}
          </g>
        </g>
      </svg>
    );
  };

  console.log("config", config);

  return (
    <>
      <Card
        className={classes.variantCard}
        onClick={handlePreviewClick}
        key={`${mapping?.profile?.sku_code}-${mapping?.material?.sku_code}-${mapping?.color?.sku_code}-${mapping?.width?.sku_code}`}
      >
        {/* Only show fullscreen button if manual warp is enabled */}
        {config?.manualWarp?.isEnabled && !!updateManualWarp && (
            <IconButton
              className={classes.fullscreenButton}
              onClick={handleFullscreenClick}
              size="small"
              style={{ right: 48 }}
            >
              <FullscreenIcon fontSize="small" />
            </IconButton>
        )}
        {/* Only show delete button if not preview */}
        {!isPreview && (
          <IconButton className={`${classes.deleteButton} delete-button`} onClick={handleDelete} size="small">
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
        {isLarge && !isPreview && (
          <Typography variant="h6" className={classes.materialTitle}>
            {`Width: ${mapping?.width?.sku_code}`}
            <Typography className={classes.widthUnit}>mm</Typography>
          </Typography>
        )}
        <div
          className={classes.imageContainer}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          ref={svgRef}
        >
          {!showPreview && renderSVG(isHovered && !showPreview)}
        </div>
        {!isPreview && <div className={classes.variantInfo}>
          <span className={classes.tag} title={String(mapping.color?.name || '')}>
            {mapping.color?.sku_code === 'NONE' ?  String(`${mapping.color?.name}: ${mapping.material?.name}`) : String(mapping.color?.name || '')}
          </span>
        </div>}
      </Card>
      <Modal open={showPreview} onClose={handleClosePreview} className={classes.modal}>
          <Box className={classes.modalContent}>
            <IconButton className={classes.closeButton} onClick={handleClosePreview}>
              <CloseIcon />
          </IconButton>
          <Box
            className={classes.previewContainer}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {showPreview && renderSVG(isHovered && showPreview)}
          </Box>
        </Box>
      </Modal>
      {/* <ManualWarpModal
        open={showManualWarpModal}
        onClose={handleCloseManualWarp}
        mapping={mapping}
        config={config}
        svgFile={svgFile}
        svgContent={svgContent}
        transforms={transforms}
        gRef={gRef}
        pathRef={pathRef}
        state={state}
        textures={textures}
        textureUrl={textureUrl}
        backgroundPatternId={backgroundPatternId}
        ringImage={ringImage}
        dispatch={dispatch}
      /> */}
      <ManualWarpModal
        open={showManualWarpModal}
        onClose={handleCloseManualWarp}
        pattern={originalPatternRef.current} // Always use original pattern from ref
        ringPath={config?.path}
        meshComplexity={config?.manualWarp?.meshComplexity || 3}
        interpolation={config?.manualWarp?.interpolation || 50}
        savedControlPoints={config?.manualWarp?.controlPoints || []}
        savedPatternPosition={config?.manualWarp?.patternPosition || { x: 0, y: 0 }}
        savedPatternScale={config?.manualWarp?.patternScale || 1}
        savedHistory={config?.manualWarp?.history || []}
        savedHistoryIndex={config?.manualWarp?.historyIndex ?? -1}
        onSave={handleSave}
      />
    </>
  );
};
export default PreviewVariantCard;
